export type AppLocale = 'ar' | 'en'
export type ExportLocale = 'reader' | AppLocale
export type DateFormat = 'DMY' | 'MDY' | 'ISO'
export type TimeFormat = '24' | '12'

export interface RegionSettings {
  currency: string
  dateFormat: DateFormat
  defaultLocale: AppLocale
  exportLocale: ExportLocale
  latinDigits: boolean
  timeFormat: TimeFormat
  units: 'metric'
}

const DEFAULTS: RegionSettings = {
  currency: 'JOD',
  dateFormat: 'DMY',
  defaultLocale: 'ar',
  exportLocale: 'reader',
  latinDigits: true,
  timeFormat: '24',
  units: 'metric',
}

let current: RegionSettings = DEFAULTS

export const getRegion = (): RegionSettings => current

export const setRegionSettings = (next: RegionSettings) => {
  current = next
}

const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩'

export const applyDigits = (value: string) =>
  current.latinDigits ? value : value.replace(/\d/g, (digit) => ARABIC_DIGITS[Number(digit)])

export const formatDateValue = (year: string, month: string, day: string) => {
  if (current.dateFormat === 'MDY') return `${month}/${day}/${year}`
  if (current.dateFormat === 'ISO') return `${year}-${month}-${day}`
  return `${day}/${month}/${year}`
}

const LOCALE_KEY = 'jopath.locale'

export const readStoredLocale = (): AppLocale | null => {
  try {
    const stored = localStorage.getItem(LOCALE_KEY)
    return stored === 'ar' || stored === 'en' ? stored : null
  } catch {
    return null
  }
}

export const storeLocale = (locale: AppLocale) => {
  try {
    localStorage.setItem(LOCALE_KEY, locale)
  } catch {
    // per-browser preference only
  }
}

/** Language for the interface, sign-in, and print preview. An explicit choice wins over the organisation default. */
export const activeLocale = (): AppLocale => readStoredLocale() ?? current.defaultLocale

/** Print preview opens in this language. "reader" follows the person's own language. */
export const printLocale = (): AppLocale => (current.exportLocale === 'reader' ? activeLocale() : current.exportLocale)
